/**
 * hreflang.ts — génère les balises hreflang SELF-REFERENCING par page.
 *
 * Contexte : donoor.org est un site FR servant plusieurs variants francophones
 * (FR/BE/CH/CA/LU) avec le même contenu, et propose en plus l'anglais,
 * l'allemand, l'espagnol, l'italien et le néerlandais sur une liste blanche de
 * pages (i18n/pagesTraduites.ts). Le hreflang aide Google à servir la bonne
 * langue/région à chaque visiteur.
 *
 * Bug corrigé (2026-08-01) : avant, 7 balises hreflang étaient hardcodées dans
 * nuxt.config vers `https://donoor.org` (la home) et appliquées à TOUTES les
 * pages → chaque page déclarait que sa version fr était la home → Google confus
 * sur l'URL à indexer (« Conflits hreflang » sur 77 pages, détecté par SemRush).
 *
 * Fix : chaque page pointe ses hreflang vers ELLE-MÊME (son URL courante).
 * Plugin universel (SSR + client) pour que les balises soient dans le HTML
 * brut (indispensable SEO).
 *
 * NB : on n'ajoute PAS de hreflang sur les pages noindex/transactionnelles
 * (/pay, /embed, etc.) — inutile et contradictoire.
 */
import { codesHreflang } from "#shared/utils/hreflang";
import { estCheminTechnique, estIndexeeEnFrancaisSeulement } from "#shared/utils/indexationLangues";
import { cheminSansLangue, LANGUES } from "~/utils/langue";

// Codes annoncés par langue (variantes francophones FR/BE/CH/CA/LU, nl-BE,
// de-BE/de-LU) : #shared/utils/hreflang, même source que le plan du site.

// Pages à NE PAS taguer (noindex / transactionnel / technique) et annuaire
// local indexé en français seulement : #shared/utils/indexationLangues.

/**
 * Une page de la liste blanche (i18n/pagesTraduites.ts) existe dans PLUSIEURS
 * langues : ses balises annoncent alors CHAQUE adresse existante (variantes FR
 * → adresse française, une balise par langue cible disponible), et x-default
 * pointe l'adresse française. Une page uniquement française garde le
 * comportement historique (toutes les balises sur elle-même).
 */
export default defineNuxtPlugin(() => {
  const route = useRoute();
  const switchLocalePath = useSwitchLocalePath();
  // URL du site via nuxt-site-config (module `site` dans nuxt.config → même source
  // que le sitemap/canonical). Fallback prod si indisponible.
  const siteUrl = (useSiteConfig().url as string) || "https://donoor.org";
  const SITE_URL = siteUrl.replace(/\/$/, "");

  const absolue = (chemin: string) => {
    const sansQuery = chemin.split(/[?#]/)[0] || "/";
    const clean = sansQuery !== "/" && sansQuery.endsWith("/") ? sansQuery.slice(0, -1) : sansQuery;
    return `${SITE_URL}${clean === "/" ? "" : clean}`;
  };

  const links = computed(() => {
    const path = cheminSansLangue(route.path);
    if (estCheminTechnique(path)) return [];
    const cheminFr = switchLocalePath("fr");
    const urlFr = absolue(cheminFr || route.path);
    const balises = codesHreflang("fr").map((hreflang) => ({ hreflang, href: urlFr }));
    // Annuaire local : ses traductions sont en noindex, ne pas les annoncer.
    const francaisSeul = estIndexeeEnFrancaisSeulement(path);
    for (const langue of francaisSeul ? [] : LANGUES) {
      if (langue === "fr") continue;
      const chemin = switchLocalePath(langue);
      // Chaîne vide = la page n'existe pas dans cette langue : pas de balise.
      if (cheminFr && chemin) {
        for (const hreflang of codesHreflang(langue)) balises.push({ hreflang, href: absolue(chemin) });
      }
    }
    balises.push({ hreflang: "x-default", href: urlFr });
    return balises.map(({ hreflang, href }) => ({
      rel: "alternate" as const,
      hreflang,
      href,
      key: `hreflang-${hreflang}`, // clé stable → pas de doublon entre navigations
    }));
  });

  useHead({ link: links });
});
